import type { FC } from 'react'
import styled from 'styled-components'

import type { ContentImage } from '@/components/patterns/content'

/** Per the Figma masks: each avatar is notched where the next overlaps, leaving a transparent gap. */
const NOTCH_GAP = 2

const Row = styled.span<{ $size: number; $overlap: number }>`
  display: inline-flex;

  flex-shrink: 0;

  > img {
    width: ${({ $size }) => $size}px;
    height: ${({ $size }) => $size}px;
    flex-shrink: 0;
    border-radius: 50%;
    object-fit: cover;

    &:not(:first-child) {
      margin-left: ${({ $overlap }) => -$overlap}px;
    }

    &:not(:last-child) {
      ${({ $size, $overlap }) => {
        const nextCentre = $size * 1.5 - $overlap
        const cut = $size / 2 + NOTCH_GAP
        const mask = `radial-gradient(circle at ${nextCentre}px 50%, transparent ${cut}px, #000 ${cut + 0.5}px)`
        return `
          -webkit-mask: ${mask};
          mask: ${mask};
        `
      }}
    }
  }
`

interface AvatarGroupProps {
  images: ContentImage[]
  /** Diameter in px. */
  size?: number
  /** How far each avatar tucks under the previous one, in px. */
  overlap?: number
  testId?: string
}

/**
 * Overlapping circle-cropped avatars — the social-proof cluster. Lives here
 * until the Figma DS grows an Avatar component to mirror into @zoe/ds-web.
 */
export const AvatarGroup: FC<AvatarGroupProps> = ({ images, size = 21, overlap = 4, testId }) => {
  if (images.length === 0) return null

  return (
    <Row $size={size} $overlap={overlap} data-testid={testId}>
      {images.map((image, index) => (
        // Indexed key: CMS clusters can repeat a URL, and the list never reorders.
        <img key={`${image.src}-${index}`} src={image.src} alt={image.alt} />
      ))}
    </Row>
  )
}
